import { useRouter } from "next/navigation";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import useResetStateTravelerEdit from "@/app/common-hooks/use-reset-state-traveler-edit/user-reset-state-traveler-edit";

import {
  AvailableCruiseOptionTypes,
  CruisePageContent,
  GroupedCruiseMagnoliaDataType,
} from "../types/cruise.type";
import { groupedCruiseMagnoliaData } from "../utils/cruise.utils";

const CruiseContext = createContext<{
  selectedAnswer: AvailableCruiseOptionTypes;
  groupedMagnoliaData: GroupedCruiseMagnoliaDataType;
  isDrawerOpen: boolean;
  isOpenUnsavedChangesModal: boolean;
  scrollRefArray: {
    ref: React.RefObject<HTMLDivElement | null>;
    isToggleWrapper?: boolean;
  }[];
  toggleWrapperRef: React.RefObject<HTMLDivElement | null>;
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpenUnsavedChangesModal: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedAnswer: React.Dispatch<
    React.SetStateAction<AvailableCruiseOptionTypes>
  >;
  isCruiseModalOpen: boolean;
  setIsCruiseModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  magnoliaData: CruisePageContent;
  handleUnSavedWarningMessageContinueBtn: () => void;
} | null>(null);

function CruiseProvider({
  children,
  initialValue,
}: Readonly<{
  children: React.ReactNode;
  initialValue: CruisePageContent;
}>) {
  const [selectedAnswer, setSelectedAnswer] =
    useState<AvailableCruiseOptionTypes>(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isOpenUnsavedChangesModal, setIsOpenUnsavedChangesModal] =
    useState<boolean>(false);
  const [isCruiseModalOpen, setIsCruiseModalOpen] = useState<boolean>(false);
  const router = useRouter();
  const { resetQuestionData } = useResetStateTravelerEdit();
  const toggleWrapperRef = useRef<HTMLDivElement>(null);

  const scrollRefArray = useMemo(() => {
    return [
      {
        ref: toggleWrapperRef,
        isToggleWrapper: true,
      },
    ];
  }, [toggleWrapperRef]);

  const groupedMagnoliaData = useMemo(() => {
    return groupedCruiseMagnoliaData(initialValue);
  }, [initialValue]);

  const handleUnSavedWarningMessageContinueBtn = useCallback(() => {
    setIsOpenUnsavedChangesModal(false);
    resetQuestionData();
    router.push("/");
  }, [resetQuestionData, router]);

  const contextValue = useMemo(
    () => ({
      magnoliaData: initialValue,
      groupedMagnoliaData,
      selectedAnswer,
      isDrawerOpen,
      isOpenUnsavedChangesModal,
      scrollRefArray,
      toggleWrapperRef,
      handleUnSavedWarningMessageContinueBtn,
      setSelectedAnswer,
      setIsDrawerOpen,
      isCruiseModalOpen,
      setIsCruiseModalOpen,
      setIsOpenUnsavedChangesModal,
    }),
    [
      initialValue,
      groupedMagnoliaData,
      selectedAnswer,
      isDrawerOpen,
      isOpenUnsavedChangesModal,
      scrollRefArray,
      toggleWrapperRef,
      handleUnSavedWarningMessageContinueBtn,
      isCruiseModalOpen,
    ]
  );

  return (
    <CruiseContext.Provider value={contextValue}>
      {children}
    </CruiseContext.Provider>
  );
}
export const useCruiseContext = () => {
  const context = useContext(CruiseContext);
  if (!context) {
    throw new Error("useCruiseContext must be used within a CruiseProvider");
  }
  return context;
};

export default CruiseProvider;
